import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/** Pure shaping for the Bill Scraping page. Kept out of the component: no jsdom here, so a render test cannot run. */

/** Hours after which a run's numbers are no longer current. */
export const STALE_AFTER_HOURS = 36;
export const PROVIDER_STATUS = {
  OK: 'ok',
  NO_BILLS: 'no-bills',
  SKIPPED: 'skipped-all',
  ERROR: 'error'
};

/** `skipped-all` is HEALTHY - nothing was due, the anti-hammer working. Not the same as `no-bills`. */
export const isProviderHealthy = provider => (provider === null || provider === void 0 ? void 0 : provider.status) === PROVIDER_STATUS.OK || (provider === null || provider === void 0 ? void 0 : provider.status) === PROVIDER_STATUS.SKIPPED;

/** Worst first. `action_required` outranks all: the classes a person can actually fix (bad credentials, an MFA wall, and our own missing config). */
export const sortProviders = providers => [...(providers || [])].sort((a, b) => {
  if (!!b.action_required !== !!a.action_required) return b.action_required ? 1 : -1;
  if (isProviderHealthy(a) !== isProviderHealthy(b)) return isProviderHealthy(a) ? 1 : -1;
  return (b.downloaded || 0) - (a.downloaded || 0);
});

/**
 * One point per run for the bills-by-date chart, oldest first so the x axis reads left to right.
 *
 * `mapped` and `posted` are the ledger's running totals at the end of that run, not that run's
 * work, so they trend rather than spike. That trend is the only progress-over-time signal available
 * without recording a nightly snapshot of its own.
 */
export const toDateSeries = runs => [...(runs || [])].filter(run => run === null || run === void 0 ? void 0 : run.finished_at).sort((a, b) => String(a.finished_at).localeCompare(String(b.finished_at))).map(run => {
  var _run$totals, _run$totals2, _run$ingest, _run$ingest2;
  return {
    date: String(run.finished_at).slice(0, 10),
    downloaded: ((_run$totals = run.totals) === null || _run$totals === void 0 ? void 0 : _run$totals.pdfs) || 0,
    uploaded: ((_run$totals2 = run.totals) === null || _run$totals2 === void 0 ? void 0 : _run$totals2.s3_uploaded) || 0,
    mapped: ((_run$ingest = run.ingest) === null || _run$ingest === void 0 ? void 0 : _run$ingest.mapped) || 0,
    posted: ((_run$ingest2 = run.ingest) === null || _run$ingest2 === void 0 ? void 0 : _run$ingest2.posted) || 0
  };
});

/** Newest run old enough that showing its numbers as current would mislead. */
export const isStale = function (runs) {
  let now = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : Date.now();
  const newest = (runs || []).find(run => run === null || run === void 0 ? void 0 : run.finished_at);
  if (!newest) return false; // no data at all is a different message, not staleness
  const finished = Date.parse(newest.finished_at);
  if (Number.isNaN(finished)) return false;
  return now - finished > STALE_AFTER_HOURS * 60 * 60 * 1000;
};

/** How a failure class reads to an operator. `configuration_error` is the one that matters most:
 *  it is OUR missing config, not a portal defending itself, and it used to hide as captcha_blocked. */
export const FAILURE_CLASS_LABELS = {
  configuration_error: 'Our config is missing',
  credential_failure: 'Bad credentials',
  mfa_required: 'MFA wall',
  captcha_blocked: 'Captcha blocked',
  infrastructure_failure: 'Network / proxy',
  fetcher_failure: 'Fetcher failed',
  ambiguous: 'Unclassified'
};

/** Why a stream has no property. The whole point of showing these is that "9% mapped" names a
 *  symptom, while the reason names the work. */
export const UNMAPPED_REASON_LABELS = {
  'no-address-for-account': 'No address for the account',
  'inherited-unconfirmed': 'Inherited from login, needs confirming',
  ambiguous: 'Too close to call',
  weak: 'No confident match',
  absent: 'Never reached the map'
};

/**
 * What the scraper fleet has actually done, across every run in the window.
 *
 * The per-run tiles report one sweep and discard the rest, which understates the fleet by the
 * number of runs: a single sweep looks like a pilot, thirty days of them is the system. Summed
 * from the same payload the page already loads, so it costs no extra request.
 */
export const fleetTotals = function () {
  var _latest$totals, _latest$totals2, _latest$totals3, _latest$totals4;
  let runs = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  const list = Array.isArray(runs) ? runs.filter(Boolean) : [];
  const latest = list[0] || {};
  return list.reduce((acc, run) => {
    var _run$totals3, _run$totals4;
    return _objectSpread(_objectSpread({}, acc), {}, {
      billsFetched: acc.billsFetched + (((_run$totals3 = run.totals) === null || _run$totals3 === void 0 ? void 0 : _run$totals3.pdfs) || 0),
      newToS3: acc.newToS3 + (((_run$totals4 = run.totals) === null || _run$totals4 === void 0 ? void 0 : _run$totals4.s3_uploaded) || 0)
    });
  }, {
    runs: list.length,
    // The registry is the build: scrapers written, whether or not a property routes to one yet.
    scrapersBuilt: ((_latest$totals = latest.totals) === null || _latest$totals === void 0 ? void 0 : _latest$totals.registry) || 0,
    providersSwept: ((_latest$totals2 = latest.totals) === null || _latest$totals2 === void 0 ? void 0 : _latest$totals2.providers) || 0,
    providersHealthy: ((_latest$totals3 = latest.totals) === null || _latest$totals3 === void 0 ? void 0 : _latest$totals3.ok) || 0,
    needingAPerson: ((_latest$totals4 = latest.totals) === null || _latest$totals4 === void 0 ? void 0 : _latest$totals4.action_required) || 0,
    billsFetched: 0,
    newToS3: 0
  });
};

/** Ingest funnel. `posted` is the number that matters: S3 is the means, the app is the end. */
export const ingestSummary = run => {
  const ingest = (run === null || run === void 0 ? void 0 : run.ingest) || {};
  const streams = ingest.streams || 0;
  const reasons = ingest.unmapped_reasons || {};
  return {
    streams,
    dated: ingest.dated || 0,
    mapped: ingest.mapped || 0,
    pending: ingest.pending || 0,
    posted: ingest.posted || 0,
    unmapped: ingest.unmapped || 0,
    // Biggest bucket first: it is the only one worth acting on.
    unmappedReasons: Object.entries(reasons).map(_ref => {
      let _ref2 = _slicedToArray(_ref, 2),
        reason = _ref2[0],
        count = _ref2[1];
      return {
        reason,
        count
      };
    }).sort((a, b) => b.count - a.count),
    mappedPercent: streams ? Math.round((ingest.mapped || 0) / streams * 100) : 0
  };
};